import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store';
import {
  selectActiveTradingAnalysis,
  selectTradingProgress,
  selectTradingUIState,
  setTradingProgressModalOpen,
  cancelTradingAnalysis,
} from '../store';
import { AnalysisProgress } from '../types/trading';

const TradingProgressModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeAnalysis = useAppSelector(selectActiveTradingAnalysis);
  const progress = useAppSelector(selectTradingProgress) as Record<string, AnalysisProgress>;
  const uiState = useAppSelector(selectTradingUIState);

  const handleClose = () => {
    dispatch(setTradingProgressModalOpen(false));
  };

  const handleCancel = () => {
    dispatch(cancelTradingAnalysis());
    handleClose();
  };

  const steps = [
    {
      key: 'detection',
      label: 'Detecting Trading Analysis',
      description: 'Analyzing AI response for trading patterns...',
    },
    {
      key: 'extraction',
      label: 'Extracting Trading Levels',
      description: 'Identifying support, resistance, and swing points...',
    },
    {
      key: 'generation',
      label: 'Generating Pine Script',
      description: 'Creating TradingView indicator code...',
    },
    {
      key: 'completion',
      label: 'Finalizing Analysis',
      description: 'Validating and preparing results...',
    },
  ];

  const getStepIcon = (stepKey: string) => {
    const stepProgress = progress[stepKey];
    if (!stepProgress) return <HourglassIcon />;

    switch (stepProgress.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <CircularProgress size={20} />;
      default:
        return <HourglassIcon />;
    }
  };

  const getActiveStep = () => {
    const stepKeys = steps.map(step => step.key);
    for (let i = stepKeys.length - 1; i >= 0; i--) {
      const stepProgress = progress[stepKeys[i]];
      if (stepProgress && stepProgress.status !== 'pending') {
        return i;
      }
    }
    return 0;
  };

  const activeStep = getActiveStep();
  const progressValues = Object.values(progress);
  const overallProgress = progressValues.length > 0 
    ? progressValues.reduce((acc, step) => acc + (step?.progress || 0), 0) / progressValues.length 
    : 0;

  return (
    <Dialog
      open={uiState.progressModalOpen && !!activeAnalysis}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={activeAnalysis?.status === 'in_progress'}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Generating Pine Script
          </Typography>
          {activeAnalysis?.status === 'in_progress' && (
            <CircularProgress size={20} />
          )}
        </Box>
        {activeAnalysis?.assetName && (
          <Typography variant="body2" color="text.secondary">
            {activeAnalysis.assetName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Overall Progress</Typography>
            <Typography variant="body2">{Math.round(overallProgress)}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={overallProgress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {activeAnalysis?.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {activeAnalysis.error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => {
            const stepProgress = progress[step.key];
            const isActive = index === activeStep;
            const isCompleted = stepProgress?.status === 'completed';
            const hasError = stepProgress?.status === 'error';

            return (
              <Step key={step.key} completed={isCompleted}>
                <StepLabel
                  icon={getStepIcon(step.key)}
                  error={hasError}
                >
                  <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                  
                  {stepProgress && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={stepProgress.progress}
                        sx={{ flex: 1, height: 4 }}
                      />
                      <Typography variant="caption">
                        {Math.round(stepProgress.progress)}%
                      </Typography>
                    </Box>
                  )}

                  {stepProgress?.error && (
                    <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                      {stepProgress.error}
                    </Alert>
                  )}

                  {stepProgress?.estimatedTime && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Estimated: {Math.round(stepProgress.estimatedTime / 1000)}s remaining
                    </Typography>
                  )}
                </StepContent>
              </Step>
            );
          })}
        </Stepper>

        {activeAnalysis?.status === 'completed' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Pine Script generated successfully! The indicator is ready for TradingView.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {activeAnalysis?.status === 'in_progress' ? (
          <Button onClick={handleCancel} color="error">
            Cancel
          </Button>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TradingProgressModal;